import { inject, Injectable, signal } from '@angular/core';
import { from, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import Client, { Local, sessions } from 'src/shared/services/client/client';
import { RegionConfigService } from 'src/shared/services/region-config/region-config.service';

export type JoiiSessionType = 'chat' | 'on-demand-video' | 'pre-booked-video' | 'home';

export function sessionTypeLabel(type: JoiiSessionType): string {
  switch (type) {
    case 'on-demand-video':
      return 'On-demand video';
    case 'pre-booked-video':
      return 'Pre-booked video';
    case 'home':
      return 'Home';
    default:
      return 'Chat';
  }
}

// Joii-issued API key for POST /v1/sessions. Prefilled for testing — ships in every portal bundle.
const DEFAULT_SESSIONS_API_KEY = 'c745e3206277bd6fe40007fee65fb76a5da7b0c102d0aef3bb127fed18dc28f3';

/**
 * Route-scoped (see joii-sessions-routes providers). Holds the sessions API key in memory
 * for the lifetime of the joii-sessions section — no localStorage.
 */
@Injectable()
export class JoiiSessionsDataService {
  private regionConfig = inject(RegionConfigService);

  readonly apiKey = signal(DEFAULT_SESSIONS_API_KEY);

  setApiKey(key: string): void {
    this.apiKey.set(typeof key === 'string' ? key : String(key ?? ''));
  }

  public createSession(
    userDocId: string,
    petDocId: string,
    type: JoiiSessionType,
    resourceDocId?: string | null
  ): Observable<sessions.CreateSessionResponseV1> {
    const request: sessions.CreateSessionRequestV1 = { pet_doc_id: petDocId, type };
    if (resourceDocId?.trim()) {
      request.resource_doc_id = resourceDocId.trim();
    }
    return from(this.apiKeyClient().sessions.CreateSession(userDocId, request));
  }

  /** Existing chats and appointments that can be resumed for the selected pet. */
  public listSessions(userDocId: string, petDocId: string): Observable<sessions.ListSessionsResponse> {
    return from(
      this.apiKeyClient().sessions.ListSessionsForPet(userDocId, {
        PetDocID: petDocId,
      })
    );
  }

  /**
   * POST /v1/sessions rejects staff JWTs — it must be called with a Joii-issued API key,
   * so this bypasses ClientService (which authenticates with the Firebase JWT). The chat
   * and appointment lookups use the same key so the whole page runs as an integrator would.
   */
  private apiKeyClient(): Client {
    const regionApi = this.regionConfig.getConfigForRegion().api;
    return new Client(regionApi !== '' ? regionApi : Local, {
      requestInit: {
        headers: {
          Authorization: `Bearer ${this.apiKey().trim()}`,
          'Accept-Language': localStorage.getItem('lang') || 'en',
          'x-platform': 'web',
          'x-platform-version': navigator.userAgent,
          'x-client-version': environment.VERSION,
        },
      },
    });
  }
}
